/* eslint-disable no-underscore-dangle */
// no-underscore-dangle: window.__npGeo is the design's one-request-per-page contract between the geo-aware cards.
// Location → routes profile for the Multiconexão card (port of section 4 of the design's app.js).
// The five rows are slots (r1–r5) filled for where the visitor is. The game server is the country's hub;
// the NoPing routes are the visitor's city, the hub and the nearest cities. Unknown country, no endpoint,
// an error or GEO_TIMEOUT: the US profile. `?mcgeo=BR|MG|Belo Horizonte` (optionally `|lat|lon`) on the
// page URL forces a location (QA).

/** PLACEHOLDER: the site's geolocation endpoint (GET, JSON `{ country, region, city, lat?, lon? }`). Empty = US profile. */
export const GEO_ENDPOINT = '';
export const GEO_TIMEOUT = 2500;

export type GeoCityTuple = [string, string, number, number];
export type GeoCountryTuple = [string, string, GeoCityTuple[]];

export interface GeoData {
  countries: Record<string, GeoCountryTuple>;
  regions?: Record<string, Record<string, string>>;
  aliases?: Record<string, string>;
}

export interface GeoCity {
  city: string;
  code: string;
  lat: number;
  lon: number;
  cc: string;
}

export interface VisitorGeo {
  country: string;
  region: string;
  city: string;
  lat: number | null;
  lon: number | null;
}

export type RouteId = 'r1' | 'r2' | 'r3' | 'r4' | 'r5';
export const ROUTE_IDS: RouteId[] = ['r1', 'r2', 'r3', 'r4', 'r5'];

export type Scenario = Record<RouteId, number> & { bad: number };

export interface GeoProfile {
  key: string;
  cc: string;
  hub: GeoCity;
  user: GeoCity | null;
  routes: GeoCity[];
  scenarios: Scenario[];
}

export const BAD_LO = 180;
export const BAD_HI = 250;

/** The design's plan B when geo-data.json cannot load: the US profile only. */
export const GEO_FALLBACK: GeoData = {
  countries: {
    US: [
      'Estados Unidos',
      'Chicago',
      [
        ['Chicago', 'CHI', 41.88, -87.63],
        ['Nova York', 'NYC', 40.71, -74.01],
        ['Washington', 'WAS', 38.9, -77.04],
        ['Atlanta', 'ATL', 33.75, -84.39],
        ['Miami', 'MIA', 25.76, -80.19],
        ['Dallas', 'DFW', 32.78, -96.8],
        ['Houston', 'HOU', 29.76, -95.37],
        ['Denver', 'DEN', 39.74, -104.99],
        ['Phoenix', 'PHX', 33.45, -112.07],
        ['Los Angeles', 'LAX', 34.05, -118.24],
        ['San Jose', 'SJC', 37.34, -121.89],
        ['Seattle', 'SEA', 47.61, -122.33],
        ['Kansas City', 'MCI', 39.1, -94.58],
        ['Minneapolis', 'MSP', 44.98, -93.27],
        ['Boston', 'BOS', 42.36, -71.06],
        ['Salt Lake City', 'SLC', 40.76, -111.89],
        ['Las Vegas', 'LAS', 36.17, -115.14],
        ['Detroit', 'DTW', 42.33, -83.05],
        ['Charlotte', 'CLT', 35.23, -80.84],
        ['St. Louis', 'STL', 38.63, -90.2],
        ['Filadélfia', 'PHL', 39.95, -75.17],
      ],
    ],
  },
  regions: {},
  aliases: {},
};

const FALLBACK_GEO: VisitorGeo = { country: 'US', region: '', city: '', lat: null, lon: null };

export function fold(text: unknown): string {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function toNumber(value: unknown): number | null {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function km(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const rad = Math.PI / 180;
  const dLat = (to.lat - from.lat) * rad;
  const dLon = (to.lon - from.lon) * rad;
  const hav = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(from.lat * rad) * Math.cos(to.lat * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 12742 * Math.asin(Math.min(1, Math.sqrt(hav)));
}

function toCity(cc: string, tuple: GeoCityTuple): GeoCity {
  return { city: tuple[0], code: tuple[1], lat: tuple[2], lon: tuple[3], cc };
}

function codeOf(name: string): string {
  const letters = fold(name).replace(/ /g, '').toUpperCase();
  return `${letters}XXX`.slice(0, 3);
}

/** Accepts the endpoint's alternate key names (`country_code`, `regionCode`, `latitude`…). */
export function normalizeGeo(input: unknown): VisitorGeo | null {
  if (!input || typeof input !== 'object') return null;
  const data = input as Record<string, unknown>;
  const countryField = data.country;
  const countryCode = data.country_code ?? data.countryCode ?? (countryField && typeof countryField === 'object' ? (countryField as Record<string, unknown>).code : countryField);
  if (!countryCode || String(countryCode).length !== 2) return null;
  return {
    country: String(countryCode).toUpperCase(),
    region: String(data.region_code ?? data.regionCode ?? data.region ?? data.state ?? ''),
    city: String(data.city ?? ''),
    lat: toNumber(data.lat ?? data.latitude),
    lon: toNumber(data.lon ?? data.lng ?? data.longitude),
  };
}

/** `?mcgeo=BR|MG|Belo Horizonte` (optionally `|lat|lon`) forces a location for QA. */
export function forcedGeo(search: string): VisitorGeo | null {
  try {
    const match = /[?&]mcgeo=([^&#]*)/.exec(search);
    if (!match?.[1]) return null;
    const parts = decodeURIComponent(match[1].replace(/\+/g, ' ')).split('|');
    return normalizeGeo({ country: parts[0], region: parts[1], city: parts[2], lat: parts[3], lon: parts[4] });
  } catch {
    return null;
  }
}

export function createProfiler(geoData: GeoData) {
  const { countries } = geoData;
  const regions = geoData.regions ?? {};
  const aliases = geoData.aliases ?? {};

  function findCity(cc: string, name: string): GeoCity | null {
    const cities = countries[cc]?.[2] ?? [];
    let folded = fold(name);
    const alias = aliases[folded];
    if (alias) folded = fold(alias);
    if (!folded) return null;
    const found = cities.find((tuple) => fold(tuple[0]) === folded);
    return found ? toCity(cc, found) : null;
  }

  return function profileFor(requested: VisitorGeo | null): GeoProfile {
    const geo = requested && countries[requested.country] ? requested : FALLBACK_GEO;
    const cc = countries[geo.country] ? geo.country : 'US';
    const meta = countries[cc] ?? (GEO_FALLBACK.countries.US as GeoCountryTuple);
    const hubTuple = meta[2].find((tuple) => tuple[0] === meta[1]) ?? meta[2][0];
    const hub = toCity(cc, hubTuple as GeoCityTuple);

    let user: GeoCity | null = null;
    const known = findCity(cc, geo.city);
    if (known) user = known;
    else if (geo.city) {
      const regionCity = regions[cc]?.[String(geo.region).toUpperCase()];
      const anchor = geo.lat != null && geo.lon != null ? { lat: geo.lat, lon: geo.lon } : ((regionCity ? findCity(cc, regionCity) : null) ?? hub);
      user = { city: String(geo.city), code: codeOf(geo.city), lat: anchor.lat, lon: anchor.lon, cc };
    }

    const origin = user ?? hub;
    const list: GeoCity[] = [];
    if (user && fold(user.city) !== fold(hub.city)) list.push(user);
    list.push(hub);
    const taken = (candidate: GeoCity) => list.some((listed) => fold(listed.city) === fold(candidate.city));
    const nearest = (pool: GeoCity[]) => pool.filter((candidate) => !taken(candidate)).sort((first, second) => km(origin, first) - km(origin, second));

    const own = nearest(meta[2].map((tuple) => toCity(cc, tuple)));
    while (list.length < 5 && own.length) list.push(own.shift() as GeoCity);
    if (list.length < 5) {
      const everywhere: GeoCity[] = [];
      Object.keys(countries).forEach((otherCc) => {
        if (otherCc === cc) return;
        countries[otherCc]?.[2].forEach((tuple) => everywhere.push(toCity(otherCc, tuple)));
      });
      const others = nearest(everywhere);
      while (list.length < 5 && others.length) list.push(others.shift() as GeoCity);
    }

    // latency: how far each city bends the path from the visitor to the hub (fibre: ~1 ms per 100 km)
    const direct = km(origin, hub);
    const rank = list.map((city, index) => ({ index, detour: km(origin, city) + km(city, hub) - direct })).sort((first, second) => first.detour - second.detour || first.index - second.index);
    const base: number[] = [];
    rank.forEach((ranked, position) => {
      base[ranked.index] = position < 2 ? 0 : Math.min(40, 6 + position + Math.round(ranked.detour / 100));
    });
    const fastest = rank[0]?.index ?? 0;
    const runnerUp = rank[1]?.index ?? 1;
    const scenarios: Scenario[] = [];
    for (let step = 0; step < 5; step += 1) {
      const row = { bad: randomInt(BAD_LO, BAD_HI) } as Scenario;
      list.forEach((_city, index) => {
        const id = ROUTE_IDS[index] as RouteId;
        if (index === fastest) row[id] = step % 2 ? randomInt(7, 10) : randomInt(2, 3);
        else if (index === runnerUp) row[id] = step % 2 ? randomInt(2, 4) : randomInt(4, 5);
        else row[id] = (base[index] ?? 0) + randomInt(0, 2);
      });
      scenarios.push(row);
    }
    return { key: `${cc}|${fold(user ? user.city : '')}`, cc, hub, user, routes: list, scenarios };
  };
}

/** One request per page, shared through `window.__npGeo` (the design's contract for sibling cards). */
export function sharedGeo(): Promise<unknown> {
  const host = window as unknown as { __npGeo?: Promise<unknown> };
  if (!host.__npGeo) {
    host.__npGeo = new Promise((resolve) => {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = window.setTimeout(() => {
        controller?.abort();
        resolve(null);
      }, GEO_TIMEOUT);
      fetch(GEO_ENDPOINT, { credentials: 'same-origin', signal: controller?.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then(
          (data) => {
            window.clearTimeout(timer);
            resolve(data);
          },
          () => {
            window.clearTimeout(timer);
            resolve(null);
          }
        );
    });
  }
  return host.__npGeo;
}
